import { state, settings, FREE_MODE_LIMIT } from './shared/state';
import { $, t, I18N, showToast } from './shared/index';
import { applyCategorySystemPrompt, buildEffectiveSystemPrompt } from './shared/system-prompt';
import { SessionManager } from './session/sessions';
import { updateContextBadge, updateWelcomeHints, loadProviderCapabilities, resizeCategorySelect } from './shared/ui';
import { loadAdminStats, loadAdminUsers, loadAdminCategories } from './admin/admin';
import { refreshBalancePanel, exportBalanceHistoryCsv, updateBalanceDisplay } from './billing/balance-panel';
import { GenerationManager } from './generation-manager';
import type { AppUser } from './shared/types';
import { applyUiModulesNav, fillUiModulesForm } from './shared/ui-modules';
import { avatarProfileBlockHint, fetchAvatarProfiles, fillAvatarProfileSelect } from './shared/avatar-profile';
import { registerViewRouter, registerRegistrationPrompt, firstMenuView } from './shared/navigation';
import { startDeferredWatch } from './conversation/deferred';
import {
  captureTelegramLinkParam,
  consumePendingTelegramLinkCode,
  refreshTelegramLinkCard,
  stopTelegramLinkPolling,
} from './telegram-link';
import { refreshApiKeysCard } from './api-keys';

export { exportBalanceHistoryCsv };

const TOKEN_LIMIT_MIN = 1024;
const TOKEN_LIMIT_STEP = 1024;
const USERNAME_RE = /^[a-zA-Z0-9_-]+$/;
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const GUEST_MODE_STORAGE_KEY = 'avgexpert_guest_mode';

export function isGuestMode() {
  return localStorage.getItem(GUEST_MODE_STORAGE_KEY) === '1';
}

export function enableGuestMode() {
  localStorage.setItem(GUEST_MODE_STORAGE_KEY, '1');
}

export function disableGuestMode() {
  localStorage.removeItem(GUEST_MODE_STORAGE_KEY);
}

export async function enterFreeMode() {
  const activityCount = parseInt(localStorage.getItem('avgexpert_free_activity_count') || '0', 10);
  if (activityCount >= FREE_MODE_LIMIT) {
    disableGuestMode();
    await updateAnonymousUI();
    showRegistrationPrompt();
    showToast(`Вы исчерпали лимит бесплатных запросов (максимум ${FREE_MODE_LIMIT}). Пожалуйста, зарегистрируйтесь.`, 'error');
    return;
  }
  enableGuestMode();
  await updateAnonymousUI();
  $('login-screen')?.classList.remove('active');
}

interface ApiErrorBody {
  errors?: Array<{ message?: string }>;
  detail?: string;
  error?: { message?: string };
}

function getUsernameErrors(username: string, minLength = 8) {
  const errors: string[] = [];
  if (username.length < minLength) errors.push(`Имя пользователя должно содержать не менее ${minLength} символов`);
  if (username.length > 64) errors.push('Имя пользователя должно содержать не более 64 символов');
  if (username && !USERNAME_RE.test(username)) errors.push('Имя пользователя может содержать только английские буквы, цифры, _ и -');
  return errors;
}

function getPasswordErrors(password: string) {
  const errors: string[] = [];
  if (password.length < 8) errors.push('Пароль должен содержать не менее 8 символов');
  if (password.length > 128) errors.push('Пароль должен содержать не более 128 символов');
  if (!/[A-Z]/.test(password)) errors.push('Пароль должен содержать хотя бы одну заглавную букву');
  if (!/[a-z]/.test(password)) errors.push('Пароль должен содержать хотя бы одну строчную букву');
  if (!/[0-9]/.test(password)) errors.push('Пароль должен содержать хотя бы одну цифру');
  if (!/[\W_]/.test(password)) errors.push('Пароль должен содержать хотя бы один специальный символ');
  return errors;
}

function getEmailErrors(email: string) {
  const errors: string[] = [];
  const trimmed = email.trim();
  if (!trimmed) {
    errors.push('E-mail обязателен для заполнения');
  } else if (!EMAIL_RE.test(trimmed)) {
    errors.push('Некорректный формат e-mail');
  }
  if (trimmed.length > 128) {
    errors.push('E-mail должен содержать не более 128 символов');
  }
  return errors;
}

function formatApiErrors(data: ApiErrorBody | null | undefined, fallback: string) {
  if (data?.errors && data.errors.length > 0) {
    return [...new Set(data.errors.map(err => err.message).filter(Boolean))].join('; ');
  }
  return data?.detail || fallback;
}

export async function checkAuth() {
  $('app')?.classList.remove('hidden');

  // Код привязки из ссылки бота — до обработки ?token=: очистка URL ниже
  // сносит строку запроса целиком, и код иначе потерялся бы (TG-ADR-3).
  captureTelegramLinkParam();

  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get('token');
  if (urlToken) {
    state.authToken = urlToken;
    localStorage.setItem('avgexpert_token', urlToken);
    const newUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, newUrl);
  }

  if (!state.authToken) {
    state.currentUser = null;
    // Первый вход (и любой возврат гостя) — сразу Free-режим, без формы
    // регистрации. Форма показывается только явным действием (кнопка «Выйти» —
    // выход из Free), при обращении к платным функциям или при исчерпании лимита.
    await enterFreeMode();
    return;
  }

  try {
    const r = await fetch('/api/users/me', { headers: { 'Authorization': 'Bearer ' + state.authToken } });
    if (r.ok) {
      state.currentUser = await r.json() as AppUser;
      await completeLogin();
    } else {
      localStorage.removeItem('avgexpert_token');
      state.authToken = null;
      state.currentUser = null;
      stopHealthPolling();
      if (isGuestMode()) {
        await enterFreeMode();
      } else {
        await updateAnonymousUI();
        showLogin();
      }
    }
  } catch {
    stopHealthPolling();
    if (isGuestMode()) {
      await enterFreeMode();
    } else {
      await updateAnonymousUI();
      showLogin();
    }
  }
}

export function showLogin() {
  showAuthModal('login');
}

export function showRegistrationPrompt() {
  void prepareRegistrationForm('register');
}

function showAuthFormError(message: string) {
  const text = $('form-error-text');
  if (text) text.textContent = message;
  $('form-error')?.classList.remove('hidden');
}

function hideAuthFormError() {
  $('form-error')?.classList.add('hidden');
  const text = $('form-error-text');
  if (text) text.textContent = '';
}

export function switchAuthTab(mode: 'login' | 'register') {
  const isLogin = mode === 'login';
  $('login-form')?.classList.toggle('hidden', !isLogin);
  $('register-form')?.classList.toggle('hidden', isLogin);

  const title = $('form-title');
  const subtitle = $('form-subtitle');
  if (title) title.textContent = isLogin ? 'Вход в систему' : 'Регистрация';
  if (subtitle) subtitle.textContent = isLogin ? 'Введите данные для входа' : 'Создайте новый аккаунт';

  $('tab-login')?.classList.toggle('auth-tab--active', isLogin);
  $('tab-register')?.classList.toggle('auth-tab--active', !isLogin);
  $('tab-login')?.setAttribute('aria-selected', String(isLogin));
  $('tab-register')?.setAttribute('aria-selected', String(!isLogin));

  $('login-switch')?.classList.toggle('hidden', !isLogin);
  $('register-switch')?.classList.toggle('hidden', isLogin);

  hideAuthFormError();

  const firstField = isLogin ? $<HTMLInputElement>('login-user') : $<HTMLInputElement>('register-user');
  setTimeout(() => firstField?.focus(), 0);
}

function showAuthModal(mode: 'login' | 'register' = 'login') {
  $('app')?.classList.remove('hidden');
  $('login-screen')?.classList.add('active');
  switchAuthTab(mode);
}

function syncGuestCategoryDefaults() {
  const conversationSessionCat = $<HTMLSelectElement>('conversation-session-category');
  const categoryName = conversationSessionCat?.value || Object.keys(state.categories || {})[0] || '';
  const cat = state.categories?.[categoryName];
  const catInputMax = parseInt(String(cat?.input_context_max), 10);
  const contextMax = Number.isFinite(catInputMax) && catInputMax >= TOKEN_LIMIT_MIN
    ? catInputMax
    : 1000000;
  state.contextSize = Math.floor(contextMax / TOKEN_LIMIT_STEP) * TOKEN_LIMIT_STEP;
  state.maxDocsAllowed = 3;
  applyCategorySystemPrompt(categoryName);
}

async function updateAnonymousUI() {
  stopHealthPolling();
  $('login-screen')?.classList.remove('active');
  $('app')?.classList.remove('hidden');
  const titleEl = $('conversation-title-category');
  const statusText = $('status-text');
  if (statusText) statusText.textContent = 'Free';
  const statusDot = $('status-dot');
  if (statusDot) statusDot.className = 'status-dot';
  $('nav-admin')?.classList.add('hidden');
  // Совет — только зарегистрированным (гости делят общий аккаунт, ADR-003).
  $('nav-council')?.classList.add('hidden');
  $('user-docs-card')?.classList.add('hidden');
  await loadPublicCategories();
  syncGuestCategoryDefaults();
  applyUiModulesNav(null, { guest: true });
  const conversationSessionCat = $<HTMLSelectElement>('conversation-session-category');
  if (titleEl) titleEl.textContent = conversationSessionCat?.value || 'Gemma AI';
  resizeCategorySelect();
  updateContextBadge();
  updateWelcomeHints();
  // Стартовый раздел гостя — тот же первый пункт меню, что и у вошедших.
  await switchView(firstMenuView());
}

async function loadPublicCategories() {
  try {
    const catRes = await fetch('/api/users/public/categories');
    if (catRes.ok) {
      state.categories = await catRes.json();
    }
  } catch (e) {
    console.error('Failed to load public categories', e);
  }

  populateGuestCategorySelect();
  void loadProviderCapabilities();
}

function populateGuestCategorySelect() {
  const conversationSessionCat = $<HTMLSelectElement>('conversation-session-category');
  if (!conversationSessionCat) return;
  const categoryNames = Object.keys(state.categories || {});
  categoryNames.sort((a, b) => {
    const idxA = state.categories?.[a]?.sort_index ?? 0;
    const idxB = state.categories?.[b]?.sort_index ?? 0;
    if (idxA !== idxB) return idxA - idxB;
    return a.localeCompare(b);
  });
  const previousValue = conversationSessionCat.value;
  conversationSessionCat.innerHTML = '';
  categoryNames.forEach(c => {
    const opt = document.createElement('option');
    opt.value = c;
    opt.textContent = c;
    conversationSessionCat.appendChild(opt);
  });
  if (previousValue && categoryNames.includes(previousValue)) {
    conversationSessionCat.value = previousValue;
  }
}

export async function prepareRegistrationForm(mode: 'register' | 'anonymous' = 'anonymous') {
  enableGuestMode();
  await loadPublicCategories();
  syncGuestCategoryDefaults();
  resizeCategorySelect();
  if (mode === 'register') {
    showAuthModal('register');
  }
}

async function resolveRegistrationCategory(): Promise<string | undefined> {
  await loadPublicCategories();
  const selected = $<HTMLSelectElement>('conversation-session-category')?.value || '';
  const allowed = Object.keys(state.categories || {});
  if (selected && allowed.includes(selected)) return selected;
  return undefined;
}

// Слайдеров персональных лимитов в интерфейсе нет (колонки users.input_context_limit
// и output_generation_limit удалены миграцией 025). Функция осталась ради другого:
// она подставляет размер контекста текущей КАТЕГОРИИ в state.contextSize, из
// которого считается бейдж «использовано N из M токенов».
export function syncCategoryContextSize() {
  const categoryName = $<HTMLSelectElement>('user-default-category')?.value || state.currentUser?.category;
  const category = state.categories?.[categoryName || ''] || {};
  const normalizeMax = (value: unknown, fallback: number) => {
    const parsed = parseInt(String(value ?? fallback), 10);
    const raw = Number.isFinite(parsed) && parsed >= TOKEN_LIMIT_MIN ? parsed : fallback;
    return Math.max(TOKEN_LIMIT_MIN, Math.floor(raw / TOKEN_LIMIT_STEP) * TOKEN_LIMIT_STEP);
  };
  const normalizeValue = (value: unknown, fallback: number, max: number) => {
    const parsed = parseInt(String(value ?? fallback), 10);
    const raw = Number.isFinite(parsed) && parsed >= TOKEN_LIMIT_MIN ? parsed : fallback;
    const clamped = Math.min(max, raw);
    return Math.max(TOKEN_LIMIT_MIN, Math.floor(clamped / TOKEN_LIMIT_STEP) * TOKEN_LIMIT_STEP);
  };
  const inputMax = normalizeMax(category.input_context_max, 1000000);
  const inputDefault = normalizeValue(category.input_context_default, inputMax, inputMax);

  state.contextSize = inputDefault;
}

function buildUserCategoryOptions(): string[] {
  if (!state.currentUser) return [];
  const allowed = state.currentUser.allowed_categories || [];
  let options = [...allowed];
  if (options.length === 0) {
    const fromApi = Object.keys(state.categories || {});
    if (fromApi.length > 0) {
      options = fromApi;
    } else if (state.currentUser.category) {
      options = [state.currentUser.category];
    }
  }
  options.sort((a, b) => {
    const idxA = state.categories?.[a]?.sort_index ?? 0;
    const idxB = state.categories?.[b]?.sort_index ?? 0;
    if (idxA !== idxB) return idxA - idxB;
    return a.localeCompare(b);
  });
  return options;
}

function fillCategorySelect(
  selectEl: HTMLSelectElement | null,
  options: string[],
  selectedCategory?: string | null
) {
  if (!selectEl) return;
  const selected = selectedCategory || state.currentUser?.category || '';
  selectEl.innerHTML = '';
  options.forEach((name) => {
    const opt = document.createElement('option');
    opt.value = name;
    opt.textContent = name;
    if (name === selected) opt.selected = true;
    selectEl.appendChild(opt);
  });
}

export async function refreshUserSettingsForm() {
  if (!state.currentUser) return;

  const usernameEl = $<HTMLInputElement>('user-username');
  if (usernameEl) usernameEl.value = state.currentUser.username || '';

  const emailEl = $<HTMLInputElement>('user-email');
  if (emailEl) emailEl.value = state.currentUser.email || '';

  const promptContainer = $('user-system-prompt-container');
  const promptEl = $<HTMLTextAreaElement>('user-system-prompt');
  if (promptContainer && promptEl) {
    const isUserAdmin = !!state.currentUser.is_admin;
    const showToUser = !!state.currentUser.show_prompt_to_user;
    if (isUserAdmin || showToUser) {
      promptEl.value = state.currentUser.system_prompt || '';
      promptContainer.classList.remove('hidden');
    } else {
      promptContainer.classList.add('hidden');
    }
  }

  try {
    const catRes = await fetch('/api/users/categories', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (catRes.ok) {
      state.categories = await catRes.json();
    }
  } catch (e) {
    console.error('Failed to load categories', e);
  }

  const categoryOptions = buildUserCategoryOptions();
  const defaultCatSel = $<HTMLSelectElement>('user-default-category');
  fillCategorySelect(defaultCatSel, categoryOptions, state.currentUser.category);
  if (defaultCatSel) {
    defaultCatSel.onchange = syncCategoryContextSize;
    syncCategoryContextSize();
  }

  const conversationSessionCat = $<HTMLSelectElement>('conversation-session-category');
  fillCategorySelect(conversationSessionCat, categoryOptions, state.currentUser.category);
  resizeCategorySelect();

  await refreshAvatarProfileField();

  // Карточка «Telegram» обновляется вместе с формой: и при входе, и при каждом
  // открытии настроек — привязка могла случиться из чата или другой вкладки.
  void refreshTelegramLinkCard();

  // Карточка «API-ключи» — тем же тактом: ключ могли выпустить или отозвать в
  // другой вкладке, а Free-сессии карточка не положена вовсе (403 её прячет).
  void refreshApiKeysCard();

  // Видеоответы Диалога — включены, пока пользователь не сказал обратного.
  const videoEl = $<HTMLInputElement>('user-dialog-video');
  if (videoEl) videoEl.checked = state.currentUser.dialog_video_enabled !== false;

  fillUiModulesForm(state.currentUser);
}

/** Реквизит «Профиль аватара (Диалог)» — то же поле, что в админке и на форме
 * Диалога. Прячем только когда профилей нет вовсе (Диалог не настроен). */
async function refreshAvatarProfileField(): Promise<void> {
  const container = $('user-avatar-profile-container');
  const select = $<HTMLSelectElement>('user-avatar-profile');
  if (!container || !select) return;

  const profiles = await fetchAvatarProfiles();
  if (!profiles.length) {
    container.classList.add('hidden');
    return;
  }
  const current = (state.currentUser?.dialog_avatar_id as string | null) || '';
  fillAvatarProfileSelect(select, profiles, current);
  container.classList.remove('hidden');

  const blockHint = avatarProfileBlockHint(profiles);
  const hint = $('user-avatar-profile-hint');
  if (hint) {
    hint.textContent = blockHint;
    hint.classList.toggle('hidden', !blockHint);
  }
}

export async function completeLogin() {
  if (!state.currentUser) return;

  disableGuestMode();
  $('login-screen')?.classList.remove('active');
  $('app')?.classList.remove('hidden');

  // Стартовый раздел выставляется только здесь (для гостей — в updateAnonymousUI):
  // всегда первый видимый пункт меню. В разметке ни один .view/.nav-btn не
  // помечен active: иначе первый кадр показывал бы не тот раздел, а затем
  // подсветка прыгала бы на фактический.
  await switchView(firstMenuView());

  const titleEl = $('conversation-title-category');
  if (titleEl) titleEl.textContent = state.currentUser.category || 'Gemma AI';

  state.contextSize = TOKEN_LIMIT_STEP;

  if (state.currentUser.category === 'Консультант') state.maxDocsAllowed = 3;
  else if (state.currentUser.category === 'Эксперт') state.maxDocsAllowed = 5;
  else state.maxDocsAllowed = 10;

  const navAdmin = $('nav-admin');
  if (navAdmin) {
    if (state.currentUser.is_admin) navAdmin.classList.remove('hidden');
    else navAdmin.classList.add('hidden');
  }

  $('user-docs-card')?.classList.remove('hidden');

  updateBalanceDisplay(state.currentUser.balance_usd ?? 0);

  await refreshUserSettingsForm();

  applyUiModulesNav(state.currentUser, { guest: isGuestMode() });

  settings.system_prompt = buildEffectiveSystemPrompt(
    $<HTMLSelectElement>('conversation-session-category')?.value || state.currentUser.category
  );

  updateContextBadge();
  updateWelcomeHints();
  refreshBalancePanel();
  startHealthPolling();
  SessionManager.loadList();
  void GenerationManager.refreshLimits();
  void GenerationManager.syncServerJobs();
  void loadProviderCapabilities();
  // Отложенная услуга — только зарегистрированным: гости делят общий аккаунт.
  void startDeferredWatch();
  // Пункт «Совет» появляется, только если услуга включена админом
  // (council_settings.enabled + живой OG-контур). Следом поднимается сторож
  // решений куратора: раунд может ждать человека сутки, и об одобрении клиент
  // должен узнать сам, а не «если догадается зайти во встречу» (C-2).
  void import('./council/council')
    .then((m) => m.refreshCouncilNav().then(() => m.startCouncilWatch()));

  // Отложенный код привязки Telegram (?tg_link=) гасится только здесь: к этому
  // моменту токен живой и пользователь подтверждён — путь один и для входа, и
  // для регистрации, и для возврата с уже сохранённым токеном.
  void consumePendingTelegramLinkCode();

  state.isInactive = false;
  resetInactivityTimer();
  bindActivityListeners();
}

function bindActivityListeners() {
  if (state.activityListenersBound) return;

  ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'].forEach(evt => {
    document.addEventListener(evt, resetInactivityTimer, { passive: true });
  });
  state.activityListenersBound = true;
}

export function resetInactivityTimer() {
  if (!state.authToken || !state.currentUser) return;

  if (state.isInactive) {
    state.isInactive = false;
    checkServerHealth();
  }

  if (state.inactivityTimeout) {
    clearTimeout(state.inactivityTimeout);
  }

  state.inactivityTimeout = setTimeout(() => {
    state.isInactive = true;
    checkServerHealth();
  }, 180000);
}

export async function handleLogin(e: Event) {
  e.preventDefault();
  const u = $<HTMLInputElement>('login-user')!.value;
  const p = $<HTMLInputElement>('login-pass')!.value;
  hideAuthFormError();

  try {
    const r = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: u, password: p })
    });
    let d: { access_token?: string; detail?: string; error?: { message?: string } } = {};
    try {
      d = await r.json();
    } catch {
      if (r.status === 404) {
        const port = window.location.port;
        showAuthFormError(port === '5173'
          ? 'API не найден. Запустите npm start (порт 8200) — Vite проксирует /api автоматически.'
          : 'API не найден (404). Откройте приложение на http://127.0.0.1:8200 и выполните npm start.');
      } else {
        showAuthFormError(r.ok
          ? 'Сервер вернул некорректный ответ'
          : `Ошибка сервера (${r.status})`);
      }
      return;
    }
    if (r.ok && d.access_token) {
      state.authToken = d.access_token;
      localStorage.setItem('avgexpert_token', state.authToken);
      await checkAuth();
    } else {
      showAuthFormError(d.detail || d.error?.message || `Ошибка сервера (${r.status})`);
    }
  } catch {
    showAuthFormError('Ошибка сети — проверьте, что сервер запущен');
  }
}

export async function handleRegister(e: Event) {
  e.preventDefault();
  const u = $<HTMLInputElement>('register-user')!.value.trim();
  const em = $<HTMLInputElement>('register-email')!.value;
  const p = $<HTMLInputElement>('register-pass')!.value;
  const pc = $<HTMLInputElement>('register-pass-confirm')!.value;
  const category = await resolveRegistrationCategory();
  hideAuthFormError();

  const errors = [
    ...getUsernameErrors(u, 8),
    ...getEmailErrors(em),
    ...getPasswordErrors(p)
  ];

  if (p !== pc) {
    errors.push('Пароли не совпадают');
  }

  if (errors.length > 0) {
    showAuthFormError([...new Set(errors)].join('; '));
    return;
  }

  try {
    const payload: Record<string, string> = {
      username: u,
      email: em,
      password: p,
      password_confirm: pc,
    };
    if (category) payload.category = category;

    const r = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const d = await r.json() as ApiErrorBody;
    if (r.ok) {
      const loginResponse = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: u, password: p })
      });
      const loginData = await loginResponse.json() as { access_token?: string; detail?: string };
      if (!loginResponse.ok) {
        throw new Error(loginData.detail || 'Регистрация успешна, но автоматический вход не выполнен');
      }
      if (loginData.access_token) {
        state.authToken = loginData.access_token;
        localStorage.setItem('avgexpert_token', state.authToken);
        checkAuth();
      }
    } else {
      showAuthFormError(formatApiErrors(d, 'Ошибка регистрации'));
    }
  } catch (err) {
    showAuthFormError(err instanceof Error ? err.message : 'Ошибка сети');
  }
}

export async function checkServerHealth() {
  if (!state.authToken || !state.currentUser) return;
  const statusDot = $('status-dot');
  const statusText = $('status-text');
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    const activeCategory = $<HTMLSelectElement>('conversation-session-category')?.value || (state.currentUser ? state.currentUser.category : '');
    const queryParam = activeCategory ? `?category=${encodeURIComponent(activeCategory)}` : '';

    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), 10000);

    const r = await fetch(`/api/providers/health${queryParam}`, {
      headers: { 'Authorization': 'Bearer ' + state.authToken },
      signal: controller.signal
    });

    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;

    if (r.status === 401) {
      localStorage.removeItem('avgexpert_token');
      state.authToken = null;
      state.currentUser = null;
      stopHealthPolling();
      await updateAnonymousUI();
      return;
    }

    if (r.ok) {
      if (!GenerationManager.isGenerating()) {
        if (statusDot) statusDot.className = 'status-dot';
        if (statusText) statusText.textContent = state.currentUser ? (state.currentUser.username || t('status_online')) : t('status_online');
      }
    } else {
      if (!GenerationManager.isGenerating()) {
        if (statusDot) statusDot.className = 'status-dot';
        if (statusText) statusText.textContent = state.currentUser ? (state.currentUser.username || t('status_online')) : t('status_online');
      }
    }
  } catch {
    if (!GenerationManager.isGenerating()) {
      if (statusDot) statusDot.className = 'status-dot offline';
      if (statusText) statusText.textContent = t('status_offline');
    }
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export function startHealthPolling() {
  stopHealthPolling();
  checkServerHealth();
  state.healthInterval = setInterval(checkServerHealth, 30000);
}

export function stopHealthPolling() {
  if (state.healthInterval) {
    clearInterval(state.healthInterval);
    state.healthInterval = null;
  }
  if (state.inactivityTimeout) {
    clearTimeout(state.inactivityTimeout);
    state.inactivityTimeout = null;
  }

  syncCategoryContextSize();
}

export async function switchView(name: string) {
  const prevView = document.querySelector<HTMLElement>('.view.active')?.id?.replace(/^view-/, '');
  if (prevView === 'avg-dialog' && name !== 'avg-dialog') {
    const avgDialog = await import('./avg-dialog/avg-dialog');
    void avgDialog.teardownAvgDialog();
  }
  // Ушли из «Совета» — частый опрос решения куратора (раз в 8 секунд) больше
  // некому показывать: он гас только по кнопке «Встречи» внутри раздела и мог
  // бить в сервер до конца сессии. Фоновый сторож остаётся: он и должен
  // догнать человека в другом разделе.
  if (prevView === 'council' && name !== 'council') {
    const council = await import('./council/council');
    council.leaveCouncilView();
  }

  document.querySelectorAll('.view').forEach(v => v.classList.remove('active'));
  document.querySelectorAll('.nav-btn').forEach(b => b.classList.remove('active'));
  $('view-' + name)?.classList.add('active');
  document.querySelector(`.nav-btn[data-view="${name}"]`)?.classList.add('active');
  $('sidebar')?.classList.remove('open');
  $('sidebar-backdrop')?.classList.remove('active');

  if (name === 'journey') {
    const journey = await import('./journey/journey');
    void journey.loadJourneys();
  }

  if (name === 'examples') {
    const examples = await import('./examples/examples');
    void examples.loadExamplesView();
  }

  if (name === 'avg-video') {
    const avgVideo = await import('./avg-video/avg-video');
    void avgVideo.loadAvgVideoView();
  }

  if (name === 'avg-dialog') {
    const avgDialog = await import('./avg-dialog/avg-dialog');
    void avgDialog.loadAvgDialogView();
  }

  if (name === 'council') {
    const council = await import('./council/council');
    void council.loadCouncilView();
  }

  if (name === 'setup' && state.authToken) {
    try {
      const r = await fetch('/api/users/me', { headers: { 'Authorization': 'Bearer ' + state.authToken } });
      if (r.ok) {
        const u = await r.json() as AppUser;
        state.currentUser = u;
        updateBalanceDisplay(u.balance_usd ?? 0);
        refreshBalancePanel();
      }
    } catch { /* ignore */ }
    await refreshUserSettingsForm();
    const docs = await import('./kb/user-documents');
    docs.loadUserDocuments();
  } else {
    const docs = await import('./kb/user-documents');
    docs.stopUserDocumentsPolling();
    // Ожидание подтверждения привязки Telegram имеет смысл только на экране
    // настроек — ушли с него, опрос статуса больше не нужен.
    stopTelegramLinkPolling();
  }

  if (name === 'admin' && state.currentUser?.is_admin) {
    loadAdminStats();
    loadAdminUsers();
    loadAdminCategories();
    if (state.adminStatsInterval) clearInterval(state.adminStatsInterval);
    state.adminStatsInterval = setInterval(loadAdminStats, 10000);
  } else {
    if (state.adminStatsInterval) {
      clearInterval(state.adminStatsInterval);
      state.adminStatsInterval = null;
    }
  }
}

// ADR-002 phase C: features navigate and open the registration prompt through
// shared/navigation; the composition root wires the real implementations here.
registerViewRouter(switchView);
registerRegistrationPrompt(showRegistrationPrompt);
